/** The API's error envelope (API.md §1): `{ error: { code, message, details?, requestId } }`. */
interface ErrorEnvelope {
  error: { code: string; message: string; details?: unknown; requestId?: string };
}

/** Codes the portal creates itself, for failures that never reached the API. */
export const CLIENT_ERROR_CODES = { network: "NETWORK", unexpected: "UNEXPECTED_RESPONSE" } as const;

/**
 * Every failed API call becomes one of these. `message` is the API's own wording, written for people, so
 * screens show it as is; `code` is for logic (e.g. CLAWBACK_TOO_HIGH), `details` carries its numbers.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Worth retrying automatically: the network, or a server error (never a 4xx). */
  get retryable(): boolean {
    return this.code === CLIENT_ERROR_CODES.network || this.status >= 500;
  }

  static fromResponse(status: number, body: unknown): ApiError {
    if (isEnvelope(body)) {
      const { code, message, details, requestId } = body.error;
      return new ApiError(status, code, message, details, requestId);
    }
    return new ApiError(
      status,
      CLIENT_ERROR_CODES.unexpected,
      status >= 500
        ? "Something went wrong on our side. Please try again."
        : "The request couldn't be completed.",
    );
  }

  static network(cause: unknown): ApiError {
    const error = new ApiError(
      0,
      CLIENT_ERROR_CODES.network,
      "Can't reach Brocoly. Check your connection and try again.",
    );
    error.cause = cause;
    return error;
  }
}

function isEnvelope(body: unknown): body is ErrorEnvelope {
  const error = (body as ErrorEnvelope | null)?.error;
  return typeof error?.code === "string" && typeof error.message === "string";
}
